import { v4 as uuidv4 } from 'uuid'
import { initializeApp } from 'firebase/app'
import { getDatabase } from 'firebase/database'
import {
  ref,
  getStorage,
  getDownloadURL,
  uploadBytesResumable,
  deleteObject,
} from 'firebase/storage'
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
} from 'firebase/auth'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
}

const app = initializeApp(firebaseConfig)
export const firebaseStorage = getStorage(app)
export const firebaseAuth = getAuth(app)
export const firebaseRtdb = getDatabase(app)
export { signInWithEmailAndPassword, signOut }

export async function uploadFile(
  file: File,
  path: string,
  fileName?: string,
): Promise<string> {
  const uniqueFilename = fileName || `${uuidv4()}-${file.name}`
  const storageRef = ref(firebaseStorage, `${path}/${uniqueFilename}`)
  const uploadTask = uploadBytesResumable(storageRef, file)

  await new Promise((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      null,
      (error) => reject(error),
      () => resolve(uploadTask.snapshot),
    )
  })

  return getDownloadURL(uploadTask.snapshot.ref)
}

export async function deleteFile(url: string): Promise<void> {
  try {
    // Extract the file path from the URL
    const urlObj = new URL(url)
    const pathSegments = urlObj.pathname.split('/')
    const filePath = pathSegments.slice(pathSegments.indexOf('o') + 1).join('/')

    // Decode the file path
    const decodedPath = decodeURIComponent(filePath)
    const storageRef = ref(firebaseStorage, decodedPath)

    await deleteObject(storageRef)
  } catch (error) {
    console.error('Error deleting file from Firebase Storage:', error)
    throw error
  }
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  try {
    const user = firebaseAuth.currentUser
    if (!user || !user.email) {
      throw new Error('User not authenticated')
    }

    // Re-authenticate user with current password
    const credential = EmailAuthProvider.credential(user.email, currentPassword)
    await reauthenticateWithCredential(user, credential)

    // Update password
    await updatePassword(user, newPassword)
  } catch (error) {
    console.error('Change password error:', error)
    throw error
  }
}

export async function verifyCurrentPassword(
  currentPassword: string,
): Promise<void> {
  try {
    const user = firebaseAuth.currentUser
    if (!user || !user.email) {
      throw new Error('User not authenticated')
    }

    // Re-authenticate user with current password to verify it
    const credential = EmailAuthProvider.credential(user.email, currentPassword)
    await reauthenticateWithCredential(user, credential)
  } catch (error) {
    console.error('Verify current password error:', error)
    throw error
  }
}
